import React, { Fragment, useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { showAlertFailure } from 'client/methods'

import AuthFormFooter from 'components/auth/AuthFormFooter'
import AuthTitle from 'components/internal/AuthTitle'
import Button from 'components/buttons/Button'
import Card from 'components/cards/Card'
import Column from 'components/base/Column'
import dx from 'lib/dashx'
import Loader from 'components/loaders/Loader'
import RadioInput from 'components/inputs/RadioInput'
import Row from 'components/base/Row'
import Stack from 'components/base/Stack'
import Text from 'components/typography/Text'
import useClientMutation from 'hooks/useClientMutation'
import useMutation from 'hooks/useMutation'
import { ADD_ACCOUNT_TOKEN, LOGIN } from 'client/state/auth'
import { SEND_MFA_CODE_MUTATION } from 'models/MFA'

const useStyles = createUseStyles({
  stepTitle: {
    marginBottom: 4
  },
  stepDescription: {
    marginBottom: 16
  },
  skipButton: {
    cursor: 'pointer'
  }
})

function MfaBackupSelectionPage() {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()

  const account = state?.account
  const session = state?.session
  const from = state?.from

  const isFromVerify = from === 'verify'
  const isEnrollmentBackup = !isFromVerify

  const phoneNumber = account?.maskedPhone

  const title = isFromVerify
    ? t('mfa.backupSelection.titleFromVerify')
    : t('mfa.backupSelection.title')

  const subtitle = isFromVerify
    ? t('mfa.backupSelection.subtitleFromVerify')
    : t('mfa.backupSelection.subtitle')

  const [method, setMethod] = useState('sms')

  const [sendMfaCode, { loading: isSending }] = useMutation(
    SEND_MFA_CODE_MUTATION,
    {
      onError: () =>
        showAlertFailure({
          message: 'Unable to send verification code.'
        })
    }
  )

  const [addAccountToken] = useClientMutation(ADD_ACCOUNT_TOKEN)
  const [login] = useClientMutation(LOGIN)
  const [finishing, setFinishing] = useState(false)

  useEffect(() => {
    if (!account || !session) {
      history.replace('/login')
    }
  }, [account, session, history])

  const handleConfirm = () => {
    if (!account) return

    if (method === 'sms') {
      if (phoneNumber) {
        sendMfaCode({
          channel: 'sms',
          target: null,
          accessToken: session.accessToken
        }).then(res => {
          const { targetMasked } = res.data.sendMfaCode

          history.push({
            pathname: '/mfa/verify',
            state: {
              account,
              session,
              mfaContext: { backup_target_masked: targetMasked },
              channel: 'sms',
              isEnrollmentBackup
            }
          })
        })

        return
      }

      history.push({
        pathname: '/mfa/backup/sms',
        state: { account, session, isEnrollmentBackup }
      })
      return
    }

    if (method === 'email') {
      if (!account.email) {
        showAlertFailure({ message: t('mfa.errors.noEmail') })
        return
      }

      sendMfaCode({
        channel: 'email',
        target: account.email,
        accessToken: session.accessToken
      }).then(() => {
        history.push({
          pathname: '/mfa/verify',
          state: {
            account,
            session,
            mfaContext: { backup_target_masked: account.email },
            channel: 'email',
            isEnrollmentBackup
          }
        })
      })
    }
  }

  const finishMfaFlow = async () => {
    try {
      setFinishing(true)

      const accountId = String(session?.accountId || account?.id)
      if (!accountId) {
        history.replace('/login')
        return
      }

      if (session?.accessToken && session?.dashxToken) {
        await addAccountToken({
          accountId,
          dashxToken: session.dashxToken,
          token: session.accessToken
        })
        dx.setIdentity(accountId, session.dashxToken)
      }

      await login({ accountId })
    } catch (e) {
      setFinishing(false)
      showAlertFailure({ message: e?.message || 'Unable to continue.' })
    }
  }

  if (finishing) return <Loader />

  return (
    <Fragment>
      <AuthTitle title={title} subtitle={subtitle} subtitlePosition="below" />

      <Row>
        <Column span={3} hiddenMd hiddenXs />
        <Column span={6} spanMd={12}>
          <Card variant="secondary">
            <Stack direction="column" align="flex-start" gutter={29}>
              {!isFromVerify && (
                <>
                  <Text
                    className={classes.stepTitle}
                    size="small"
                    weight="medium"
                    color="light"
                  >
                    {t('mfa.backupSelection.stepTitle')}
                  </Text>
                  <Text
                    className={classes.stepDescription}
                    size="xSmall"
                    weight="regular"
                    color="light"
                  >
                    {t('mfa.backupSelection.stepDescription')}
                  </Text>
                </>
              )}

              <Stack direction="column" align="flex-start">
                {/* <RadioInput
                  name="backup-method"
                  value="sms"
                  label={t('mfa.backupSelection.smsOption')}
                  subLabel={t('mfa.backupSelection.smsOptionSub')}
                  checked={method === 'sms'}
                  onChange={() => setMethod('sms')}
                  textColor="light"
                  textSize="small"
                  textWeight="medium"
                  subLabelColor="light"
                  subLabelSize="xSmall"
                  subLabelWeight="regular"
                /> */}

                <RadioInput
                  name="backup-method"
                  value="email"
                  label={t('mfa.backupSelection.emailOption')}
                  subLabel={t('mfa.backupSelection.emailOptionSub')}
                  checked={method === 'email'}
                  onChange={() => setMethod('email')}
                  textColor="light"
                  textSize="small"
                  textWeight="medium"
                  subLabelColor="light"
                  subLabelSize="xSmall"
                  subLabelWeight="regular"
                />
              </Stack>

              {!isFromVerify && (
                <Text
                  color="tertiary"
                  onClick={finishMfaFlow}
                  className={classes.skipButton}
                >
                  {t('mfa.backupSelection.skip')}
                </Text>
              )}
            </Stack>

            <AuthFormFooter>
              <Button
                onClick={handleConfirm}
                label={t('mfa.backupSelection.confirmButton')}
                disabled={isSending}
              />
            </AuthFormFooter>
          </Card>
        </Column>
      </Row>
    </Fragment>
  )
}

export default MfaBackupSelectionPage
